import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export default function PartnerSearchPrecision({
    value,
    onChange,
    placeholder = "Selecionar parceiro...",
    tipo = "todos",
    className = ""
}) {
    const [open, setOpen] = useState(false);
    const propriedadeId = localStorage.getItem("propriedade_selecionada");

    const { data: parceiros = [], isLoading } = useQuery({
        queryKey: ['parceiros_search', propriedadeId],
        queryFn: () => propriedadeId
            ? agrogestao.entities.Parceiro.filter({ propriedade_id: propriedadeId })
            : Promise.resolve([]),
        enabled: !!propriedadeId
    });

    const filteredParceiros = useMemo(() => {
        if (tipo === "todos") return parceiros;
        return parceiros.filter(p => {
            const t = (p.tipo || "outro").toLowerCase();
            if (tipo === "fornecedor") return t === "fornecedor" || t === "produtor";
            return t === tipo;
        });
    }, [parceiros, tipo]);

    const selectedPartnerLabel = useMemo(() => {
        if (!value) return "";
        const p = parceiros.find(p => {
            const label = (p.nome_razao || p.razao_social || p.nome || p.cnpj_cpf || "").trim();
            return label === value;
        });
        return p ? (p.nome_razao || p.razao_social || p.nome || p.cnpj_cpf) : value;
    }, [value, parceiros]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-full justify-between rounded-md border border-stone-200 bg-white hover:bg-stone-50 px-3 py-2 h-10 transition-all text-sm font-normal text-left",
                        !value && "text-stone-400",
                        className
                    )}
                >
                    <span className="truncate pr-2">
                        {selectedPartnerLabel || placeholder}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[400px] p-0 rounded-none border-stone-200 shadow-xl" align="start">
                <Command className="rounded-none">
                    <CommandInput placeholder="Buscar por nome ou CPF/CNPJ..." className="h-10 border-none focus:ring-0" />
                    <CommandList className="max-h-[300px]">
                        <CommandEmpty>Nenhum parceiro encontrado.</CommandEmpty>
                        <CommandGroup>
                            {filteredParceiros.map((p) => {
                                const label = (p.nome_razao || p.razao_social || p.nome).trim() || p.cnpj_cpf;
                                return (
                                    <CommandItem
                                        key={p.id}
                                        value={`${label} ${p.cnpj_cpf || ""}`}
                                        onSelect={() => {
                                            onChange(label);
                                            setOpen(false);
                                        }}
                                        className="rounded-none cursor-pointer py-3"
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4 text-[#1B4332]",
                                                value === label ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        <div className="flex flex-col gap-0.5">
                                            <span className="font-medium text-stone-900 leading-tight">{label}</span>
                                            {p.cnpj_cpf && <span className="text-[10px] text-stone-500 font-mono tracking-tighter">{p.cnpj_cpf}</span>}
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
